import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, CheckCircle, ArrowLeft, Sparkles, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format, addDays } from 'date-fns';
import { useSearchParams } from 'react-router-dom';

const API = '/api';

// Timezone display names mapping
const TIMEZONE_LABELS = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Phoenix': 'AZ',
  'America/Los_Angeles': 'PT',
  'America/Anchorage': 'AKT',
  'Pacific/Honolulu': 'HT',
  'America/Toronto': 'ET',
  'America/Vancouver': 'PT',
  'America/Edmonton': 'MT',
  'Europe/London': 'GMT',
  'Europe/Paris': 'CET',
  'Europe/Amsterdam': 'CET',
  'Asia/Dubai': 'GST',
  'Asia/Kolkata': 'IST',
  'Asia/Singapore': 'SGT',
  'Asia/Tokyo': 'JST',
  'Asia/Shanghai': 'CST',
  'Australia/Sydney': 'AEST',
  'Australia/Melbourne': 'AEST',
  'Pacific/Auckland': 'NZST',
  'Africa/Lagos': 'WAT',
  'Africa/Johannesburg': 'SAST',
  'America/Sao_Paulo': 'BRT',
  'America/Mexico_City': 'CST',
  'UTC': 'UTC'
};

const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const bookingType = searchParams.get('type') || 'first_coaching_call';
  
  const [step, setStep] = useState(1);
  const [leadInfo, setLeadInfo] = useState({ name: '', email: '', phone: '' });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [dates, setDates] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [booked, setBooked] = useState(false);
  const [adminTimezone, setAdminTimezone] = useState('America/New_York');
  const [timezoneLabel, setTimezoneLabel] = useState('ET');
  const [leadTimezone, setLeadTimezone] = useState('');
  const [leadTimezoneLabel, setLeadTimezoneLabel] = useState('');
  const [showTimezoneConfirm, setShowTimezoneConfirm] = useState(false);
  const [localTimeEquivalent, setLocalTimeEquivalent] = useState('');

  // Survey form data
  const [surveyData, setSurveyData] = useState({
    city: '',
    province_state: '',
    country: '',
    desire_for_change: 5,
    when_realized_change: '',
    changes_looking_for: [],
    money_needed_monthly: '',
    money_needed_yearly: '',
    why_online_business: '',
    marketing_budget: '',
    marketing_budget_other: '',
    readiness_level: '',
    acknowledged: false
  });

  // Fetch admin timezone on mount and detect lead's timezone
  useEffect(() => {
    const fetchTimezone = async () => {
      try {
        const response = await axios.get(`${API}/content/settings`);
        if (response.data?.timezone) {
          setAdminTimezone(response.data.timezone);
          setTimezoneLabel(TIMEZONE_LABELS[response.data.timezone] || response.data.timezone);
        }
      } catch (error) {
        console.error('Failed to fetch timezone settings');
      }
    };
    fetchTimezone();
    
    // Detect lead's browser timezone
    try {
      const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setLeadTimezone(detectedTz);
      setLeadTimezoneLabel(TIMEZONE_LABELS[detectedTz] || detectedTz);
    } catch (error) {
      console.error('Failed to detect timezone');
    }
  }, []);

  // Function to convert time from admin timezone to lead's local timezone
  const convertToLeadTimezone = (date, time) => {
    if (!date || !time || !adminTimezone || !leadTimezone) return '';
    
    try {
      const [hours, minutes] = time.split(':');
      const dateStr = format(date, 'yyyy-MM-dd');
      
      // Create a date string in the admin's timezone
      const adminDateTime = new Date(`${dateStr}T${hours.padStart(2, '0')}:${minutes}:00`);
      
      // Format in admin timezone to get the actual moment
      const adminFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: adminTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      
      // Parse the admin time parts
      const adminParts = adminFormatter.formatToParts(adminDateTime);
      const adminYear = adminParts.find(p => p.type === 'year')?.value;
      const adminMonth = adminParts.find(p => p.type === 'month')?.value;
      const adminDay = adminParts.find(p => p.type === 'day')?.value;
      const adminHour = adminParts.find(p => p.type === 'hour')?.value;
      const adminMinute = adminParts.find(p => p.type === 'minute')?.value;
      
      // Create a proper UTC date from the admin timezone
      const utcDate = new Date(Date.UTC(
        parseInt(adminYear),
        parseInt(adminMonth) - 1,
        parseInt(adminDay),
        parseInt(adminHour),
        parseInt(adminMinute)
      ));
      
      // Adjust for admin timezone offset
      const adminOffset = getTimezoneOffset(adminTimezone, utcDate);
      utcDate.setMinutes(utcDate.getMinutes() + adminOffset);
      
      // Format in lead's timezone
      const leadFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: leadTimezone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });
      
      return leadFormatter.format(utcDate);
    } catch (error) {
      console.error('Timezone conversion error:', error);
      return '';
    }
  };

  // Helper to get timezone offset in minutes
  const getTimezoneOffset = (tz, date) => {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: tz }));
    return (utcDate - tzDate) / 60000;
  };

  const changesOptions = [
    'Increase income',
    'More time with family/friends',
    'More time to travel',
    'Own my own business',
    'More money to help family'
  ];

  const budgetOptions = [
    '$0 - $100 (Starting with organic strategies)',
    '$100 - $500',
    '$500 and above'
  ];

  const readinessOptions = [
    'Ready and excited to work with you',
    'Just curious',
    'Seeing my options'
  ];

  // Determine page title based on booking type
  const isNextStepsCall = bookingType === 'next_steps_call';
  const pageTitle = isNextStepsCall ? 'Book Your Next Steps Call' : 'Book Your First Inquiry Call';
  const pageSubtitle = isNextStepsCall 
    ? 'Continue your journey with a follow-up coaching session'
    : 'Schedule your FREE 30-minute inquiry call';

  useEffect(() => {
    // Generate next 14 days
    const today = new Date();
    const next14Days = [];
    for (let i = 0; i < 14; i++) {
      next14Days.push(addDays(today, i));
    }
    setDates(next14Days);
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async (date) => {
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/available-slots?date=${dateStr}`);
      setAvailableSlots(response.data.available_slots);
    } catch (error) {
      toast.error('Failed to fetch available slots');
    }
  };

  const handleLeadInfoSubmit = async (e) => {
    e.preventDefault();
    
    // For first coaching call, check eligibility before proceeding
    if (!isNextStepsCall) {
      try {
        const response = await axios.get(`${API}/appointments/check-eligibility?email=${encodeURIComponent(leadInfo.email)}&booking_type=first_coaching_call`);
        
        if (!response.data.eligible) {
          if (response.data.reason === 'already_completed') {
            toast.error(response.data.message);
            setTimeout(() => {
              window.location.href = '/book?type=next_steps_call';
            }, 2000);
            return;
          } else if (response.data.reason === 'already_scheduled') {
            toast.error(`${response.data.message} Scheduled for ${response.data.scheduled_date} at ${response.data.scheduled_time}`);
            return;
          }
        }
      } catch (error) {
        console.error('Eligibility check failed:', error);
      }
    }
    
    // Move to calendar step
    setStep(2);
  };

  const handleCalendarContinue = () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Please select both date and time');
      return;
    }
    
    // Calculate local time equivalent and show confirmation popup
    const localTime = convertToLeadTimezone(selectedDate, selectedTime);
    setLocalTimeEquivalent(localTime);
    
    // Only show popup if lead's timezone is different from admin's timezone
    if (leadTimezone && leadTimezone !== adminTimezone) {
      setShowTimezoneConfirm(true);
    } else {
      // Same timezone, proceed directly
      setStep(3);
    }
  };

  const handleTimezoneConfirm = () => {
    setShowTimezoneConfirm(false);
    setStep(3);
  };

  const handleCheckboxChange = (option) => {
    setSurveyData(prev => ({
      ...prev,
      changes_looking_for: prev.changes_looking_for.includes(option)
        ? prev.changes_looking_for.filter(item => item !== option)
        : [...prev.changes_looking_for, option]
    }));
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    
    // Validate required survey fields
    if (!surveyData.city || !surveyData.province_state || !surveyData.country) {
      toast.error('Please fill in your location details');
      return;
    }
    if (!surveyData.when_realized_change) {
      toast.error('Please tell us when you realized your life needed to change');
      return;
    }
    if (!surveyData.why_online_business) {
      toast.error('Please tell us why you chose to start an online business');
      return;
    }
    if (!surveyData.marketing_budget) {
      toast.error('Please select your marketing budget');
      return;
    }
    if (!surveyData.readiness_level) {
      toast.error('Please tell us your readiness level');
      return;
    }
    if (!surveyData.acknowledged) {
      toast.error('Please acknowledge that you have opted for your inquiry call');
      return;
    }

    setIsSubmitting(true);
    try {
      // First create/get lead
      const leadResponse = await axios.post(`${API}/leads`, {
        name: leadInfo.name,
        email: leadInfo.email,
        phone: leadInfo.phone || 'N/A'
      });

      // Book appointment with survey data included
      await axios.post(`${API}/appointments`, {
        lead_id: leadResponse.data.id,
        lead_email: leadInfo.email,
        lead_name: leadInfo.name,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime,
        booking_type: bookingType,
        survey_data: surveyData,
        lead_timezone: leadTimezone  // Include lead's timezone for email conversion
      });

      // Track Schedule event in Facebook Pixel
      if (window.fbq) {
        window.fbq('track', 'Schedule', {
          content_name: isNextStepsCall ? 'Next Steps Call Booking' : 'First Inquiry Call Booking',
          content_category: 'Appointment'
        });
      }

      setBooked(true);
      toast.success('Appointment booked! Check your email for details.');
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to book appointment');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTimeSlot = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm} ${timezoneLabel}`;
  };

  if (booked) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8 lg:p-12 border border-[#E5E5E5] text-center"
          data-testid="booking-success"
        >
          <CheckCircle className="w-20 h-20 text-[#0055FF] mx-auto mb-6" />
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight mb-4">Appointment Confirmed!</h1>
          <p className="text-lg text-[#525252] mb-2">
            Your inquiry call is scheduled for:
          </p>
          <p className="text-2xl font-bold text-[#FF4500] mb-6">
            {format(selectedDate, 'EEEE, MMMM d, yyyy')} at {formatTimeSlot(selectedTime)}
          </p>
          <p className="text-base text-[#525252] mb-8">
            We've sent you a confirmation email with the Zoom meeting details.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 bg-[#FF4500] text-white px-8 py-4 rounded-lg hover:bg-[#E03E00] transition-all duration-300 font-semibold shadow-[0_8px_16px_rgba(255,69,0,0.3)]"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD]">
      {/* Timezone Confirmation Modal */}
      {showTimezoneConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-md w-full"
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-[#0055FF]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Confirm Your Time</h3>
              <p className="text-[#525252] mb-4">
                You selected:
              </p>
              <div className="bg-[#FFF3E0] rounded-lg p-4 mb-4">
                <p className="text-lg font-semibold text-[#FF4500]">
                  {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
                </p>
                <p className="text-xl font-bold text-[#0A0A0A]">
                  {selectedTime && formatTimeSlot(selectedTime)}
                </p>
              </div>
              
              {localTimeEquivalent && leadTimezone !== adminTimezone && (
                <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
                  <p className="text-sm text-blue-700 mb-1">In your local time ({leadTimezoneLabel}):</p>
                  <p className="text-lg font-bold text-blue-900">
                    {localTimeEquivalent}
                  </p>
                </div>
              )}
              
              <p className="text-sm text-[#525252] mb-6">
                Please make sure you're available at this time. The call will be conducted in the timezone shown above.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowTimezoneConfirm(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-[#525252] hover:bg-gray-50 transition-all font-semibold"
                >
                  Go Back
                </button>
                <button
                  onClick={handleTimezoneConfirm}
                  className="flex-1 px-4 py-3 bg-[#FF4500] text-white rounded-lg hover:bg-[#E03E00] transition-all font-semibold"
                  data-testid="confirm-timezone-btn"
                >
                  Confirm & Continue
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl backdrop-saturate-150 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-2">
              <Sparkles className="w-8 h-8 text-[#FF4500]" />
              <h1 className="text-2xl font-black text-[#0A0A0A] tracking-tight">MNA Consult</h1>
            </a>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 lg:p-12 border border-[#E5E5E5]"
        >
          {/* Progress Indicator - 3 Steps */}
          <div className="flex items-center justify-center gap-2 sm:gap-4 mb-8">
            <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full ${step >= 1 ? 'bg-[#FF4500] text-white' : 'bg-gray-200 text-gray-500'} font-bold text-sm sm:text-base`}>
              1
            </div>
            <div className={`h-1 w-8 sm:w-16 ${step >= 2 ? 'bg-[#FF4500]' : 'bg-gray-200'}`}></div>
            <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full ${step >= 2 ? 'bg-[#FF4500] text-white' : 'bg-gray-200 text-gray-500'} font-bold text-sm sm:text-base`}>
              2
            </div>
            <div className={`h-1 w-8 sm:w-16 ${step >= 3 ? 'bg-[#FF4500]' : 'bg-gray-200'}`}></div>
            <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full ${step >= 3 ? 'bg-[#FF4500] text-white' : 'bg-gray-200 text-gray-500'} font-bold text-sm sm:text-base`}>
              3
            </div>
          </div>

          {/* Step Labels */}
          <div className="flex justify-center gap-4 sm:gap-12 mb-8 text-xs sm:text-sm text-[#525252]">
            <span className={step === 1 ? 'text-[#FF4500] font-semibold' : ''}>Your Info</span>
            <span className={step === 2 ? 'text-[#FF4500] font-semibold' : ''}>Schedule</span>
            <span className={step === 3 ? 'text-[#FF4500] font-semibold' : ''}>Survey</span>
          </div>

          {/* STEP 1: Contact Information */}
          {step === 1 && (
            <>
              <div className="text-center mb-8">
                <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mb-4">{pageTitle}</h2>
                <p className="text-lg text-[#525252]">{pageSubtitle}</p>
                {isNextStepsCall && (
                  <div className="mt-4 inline-block bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-semibold">
                    Next Steps Call
                  </div>
                )}
              </div>

              {/* Refund Policy Disclaimer for Next Steps Call */}
              {isNextStepsCall && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6" data-testid="refund-disclaimer">
                  <div className="flex items-start gap-3">
                    <div className="text-amber-600 text-xl">$</div>
                    <div>
                      <h4 className="font-semibold text-amber-800 mb-1">Important: About This Call</h4>
                      <p className="text-sm text-amber-700 mb-2">
                        <span className="font-bold">This is NOT a coaching call.</span> This call is focused on confirming your registration details and paying your fully refundable registration fee.
                      </p>
                      <p className="text-sm text-amber-700 mb-2">
                        The $149 coaching program fee is <span className="font-bold">fully refundable within 30 days</span> of payment. 
                        After 30 days, the payment becomes <span className="font-bold">non-refundable</span>. 
                        You will receive reminder emails as your refund deadline approaches.
                      </p>
                      <p className="text-sm text-amber-700">
                        If you wish to proceed with a coaching call ahead of this step, please{' '}
                        <a href="/book?type=first_coaching_call" className="font-bold text-[#FF4500] hover:underline">
                          book your first call
                        </a>.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleLeadInfoSubmit} className="space-y-6" data-testid="booking-form-step1">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#0A0A0A] mb-2">Full Name</label>
                  <input
                    id="name"
                    type="text"
                    required
                    value={leadInfo.name}
                    onChange={(e) => setLeadInfo({ ...leadInfo, name: e.target.value })}
                    className="w-full h-12 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500] transition-all duration-300"
                    placeholder="Enter your full name"
                    data-testid="booking-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#0A0A0A] mb-2">Email Address</label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={leadInfo.email}
                    onChange={(e) => setLeadInfo({ ...leadInfo, email: e.target.value })}
                    className="w-full h-12 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500] transition-all duration-300"
                    placeholder="Enter your email"
                    data-testid="booking-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-[#0A0A0A] mb-2">Phone Number (Optional)</label>
                  <input
                    id="phone"
                    type="tel"
                    value={leadInfo.phone}
                    onChange={(e) => setLeadInfo({ ...leadInfo, phone: e.target.value })}
                    className="w-full h-12 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500] transition-all duration-300"
                    placeholder="Enter your phone number"
                    data-testid="booking-phone-input"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-[#FF4500] text-white h-12 rounded-lg hover:bg-[#E03E00] transition-all duration-300 font-semibold shadow-[0_8px_16px_rgba(255,69,0,0.3)]"
                  data-testid="continue-to-calendar-button"
                >
                  Continue to Calendar
                </button>
              </form>
            </>
          )}

          {/* STEP 2: Calendar Selection */}
          {step === 2 && (
            <>
              <div className="text-center mb-8">
                <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mb-4">Select Date & Time</h2>
                <p className="text-lg text-[#525252]">Choose a time that works best for you (All times in {timezoneLabel})</p>
              </div>

              {/* Date Selection */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-[#FF4500]" />
                  <h3 className="text-lg font-semibold">Select Date</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {dates.map((date, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setSelectedDate(date);
                        setSelectedTime(null);
                      }}
                      className={`p-4 rounded-lg border transition-all duration-300 ${
                        selectedDate && format(selectedDate, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
                          ? 'bg-[#FF4500] text-white border-[#FF4500]'
                          : 'bg-white border-[#E5E5E5] hover:border-[#FF4500] hover:shadow-md'
                      }`}
                      data-testid={`date-option-${idx}`}
                    >
                      <div className="text-sm font-medium">{format(date, 'EEE')}</div>
                      <div className="text-lg font-bold">{format(date, 'd')}</div>
                      <div className="text-xs">{format(date, 'MMM')}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Time Selection */}
              {selectedDate && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="w-5 h-5 text-[#0055FF]" />
                    <h3 className="text-lg font-semibold">Select Time</h3>
                  </div>
                  {availableSlots.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {availableSlots.map((slot, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedTime(slot)}
                          className={`p-3 rounded-lg border transition-all duration-300 ${
                            selectedTime === slot
                              ? 'bg-[#0055FF] text-white border-[#0055FF]'
                              : 'bg-white border-[#E5E5E5] hover:border-[#0055FF] hover:shadow-md'
                          }`}
                          data-testid={`time-slot-${idx}`}
                        >
                          {formatTimeSlot(slot)}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[#525252] text-center py-8">No available slots for this date. Please select another date.</p>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 bg-gray-200 text-[#0A0A0A] h-12 rounded-lg hover:bg-gray-300 transition-all duration-300 font-semibold"
                  data-testid="back-button"
                >
                  Back
                </button>
                <button
                  onClick={handleCalendarContinue}
                  disabled={!selectedDate || !selectedTime}
                  className="flex-1 bg-[#FF4500] text-white h-12 rounded-lg hover:bg-[#E03E00] transition-all duration-300 font-semibold shadow-[0_8px_16px_rgba(255,69,0,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="continue-to-survey-button"
                >
                  Continue to Survey
                </button>
              </div>
            </>
          )}

          {/* STEP 3: Pre-Call Survey (Final Step Before Submit) */}
          {step === 3 && (
            <>
              <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <ClipboardList className="w-8 h-8 text-[#FF4500]" />
                  <h2 className="text-2xl lg:text-3xl font-bold tracking-tight">Pre-Call Survey</h2>
                </div>
                <p className="text-[#525252]">Please complete this survey to finalize your booking</p>
                <div className="mt-4 inline-block bg-blue-50 text-blue-800 px-4 py-2 rounded-lg text-sm">
                  Your call: <strong>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</strong> at <strong>{formatTimeSlot(selectedTime)}</strong>
                </div>
              </div>

              <form onSubmit={handleBooking} className="space-y-6" data-testid="booking-form-step3-survey">
                {/* Location Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-[#0A0A0A] border-b pb-2">Your Location</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={surveyData.city}
                        onChange={(e) => setSurveyData({...surveyData, city: e.target.value})}
                        className="w-full h-12 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                        placeholder="Your city"
                        data-testid="survey-city-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                        Province/State <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={surveyData.province_state}
                        onChange={(e) => setSurveyData({...surveyData, province_state: e.target.value})}
                        className="w-full h-12 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                        placeholder="Your province/state"
                        data-testid="survey-state-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                        Country <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={surveyData.country}
                        onChange={(e) => setSurveyData({...surveyData, country: e.target.value})}
                        className="w-full h-12 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                        placeholder="Your country"
                        data-testid="survey-country-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Desire for Change */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-[#0A0A0A] border-b pb-2">Your Motivation</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-4">
                      How Strong Is Your Desire For Change? <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-[#525252]">Strong</span>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={surveyData.desire_for_change}
                        onChange={(e) => setSurveyData({...surveyData, desire_for_change: parseInt(e.target.value)})}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#FF4500]"
                        data-testid="survey-desire-slider"
                      />
                      <span className="text-sm text-[#525252]">Very Strong</span>
                    </div>
                    <p className="text-center text-2xl font-bold text-[#FF4500] mt-2">{surveyData.desire_for_change}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                      When did you realize that your life needed to change? <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      required
                      value={surveyData.when_realized_change}
                      onChange={(e) => setSurveyData({...surveyData, when_realized_change: e.target.value})}
                      className="w-full h-24 px-4 py-3 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500] resize-none"
                      placeholder="Share your story..."
                      data-testid="survey-realization-input"
                    />
                  </div>
                </div>

                {/* Changes Looking For */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-[#0A0A0A] border-b pb-2">What changes are you looking for?</h3>
                  <p className="text-sm text-[#525252]">Select all that apply:</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {changesOptions.map((option) => (
                      <label key={option} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={surveyData.changes_looking_for.includes(option)}
                          onChange={() => handleCheckboxChange(option)}
                          className="w-5 h-5 rounded border-gray-300 text-[#FF4500] focus:ring-[#FF4500]"
                        />
                        <span className="text-[#0A0A0A] text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Money Needed */}
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-[#0A0A0A]">
                    How much money would you need to make all of those things possible?
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-[#525252] mb-1">Per Month</label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#525252]">$</span>
                        <input
                          type="text"
                          value={surveyData.money_needed_monthly}
                          onChange={(e) => setSurveyData({...surveyData, money_needed_monthly: e.target.value})}
                          className="w-full h-12 pl-8 pr-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                          placeholder="0"
                          data-testid="survey-monthly-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-[#525252] mb-1">Per Year</label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#525252]">$</span>
                        <input
                          type="text"
                          value={surveyData.money_needed_yearly}
                          onChange={(e) => setSurveyData({...surveyData, money_needed_yearly: e.target.value})}
                          className="w-full h-12 pl-8 pr-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                          placeholder="0"
                          data-testid="survey-yearly-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Why Online Business */}
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                    Why choose to start an online business right now? <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    value={surveyData.why_online_business}
                    onChange={(e) => setSurveyData({...surveyData, why_online_business: e.target.value})}
                    className="w-full h-24 px-4 py-3 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500] resize-none"
                    placeholder="Tell us your reasons..."
                    data-testid="survey-why-input"
                  />
                </div>

                {/* Marketing Budget */}
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-[#0A0A0A]">
                    What is your monthly marketing budget? <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-[#525252]">
                    (If you're starting with little to no budget, don't worry - we'll also teach you free, organic marketing strategies to help you grow.)
                  </p>
                  
                  <div className="space-y-3">
                    {budgetOptions.map((option) => (
                      <label key={option} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="marketing_budget"
                          value={option}
                          checked={surveyData.marketing_budget === option}
                          onChange={(e) => setSurveyData({...surveyData, marketing_budget: e.target.value})}
                          className="w-5 h-5 border-gray-300 text-[#FF4500] focus:ring-[#FF4500]"
                        />
                        <span className="text-[#0A0A0A] text-sm">{option}</span>
                      </label>
                    ))}
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="marketing_budget"
                        value="other"
                        checked={surveyData.marketing_budget === 'other'}
                        onChange={(e) => setSurveyData({...surveyData, marketing_budget: e.target.value})}
                        className="w-5 h-5 border-gray-300 text-[#FF4500] focus:ring-[#FF4500]"
                      />
                      <span className="text-[#0A0A0A] text-sm">Other:</span>
                      <input
                        type="text"
                        value={surveyData.marketing_budget_other}
                        onChange={(e) => setSurveyData({...surveyData, marketing_budget_other: e.target.value, marketing_budget: 'other'})}
                        className="flex-1 h-10 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                        placeholder="Specify amount"
                      />
                    </div>
                  </div>
                </div>

                {/* Readiness Level */}
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-[#0A0A0A]">
                    Where are you at right now in terms of moving forward? <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-[#525252]">(No pressure - this helps us tailor the coaching experience to your needs)</p>
                  
                  <div className="space-y-3">
                    {readinessOptions.map((option) => (
                      <label key={option} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="readiness_level"
                          value={option}
                          checked={surveyData.readiness_level === option}
                          onChange={(e) => setSurveyData({...surveyData, readiness_level: e.target.value})}
                          className="w-5 h-5 border-gray-300 text-[#FF4500] focus:ring-[#FF4500]"
                        />
                        <span className="text-[#0A0A0A] text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Acknowledgment */}
                <div className="bg-[#F4F4F5] rounded-lg p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={surveyData.acknowledged}
                      onChange={(e) => setSurveyData({...surveyData, acknowledged: e.target.checked})}
                      className="w-5 h-5 mt-0.5 rounded border-gray-300 text-[#FF4500] focus:ring-[#FF4500]"
                      data-testid="survey-acknowledge-checkbox"
                    />
                    <span className="text-sm text-[#0A0A0A]">
                      By checking this box you acknowledge that you have opted for your inquiry call today
                    </span>
                  </label>
                </div>

                {/* Navigation Buttons */}
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="flex-1 bg-gray-200 text-[#0A0A0A] h-12 rounded-lg hover:bg-gray-300 transition-all duration-300 font-semibold"
                    data-testid="back-to-calendar-button"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-[#FF4500] text-white h-12 rounded-lg hover:bg-[#E03E00] transition-all duration-300 font-semibold shadow-[0_8px_16px_rgba(255,69,0,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="book-appointment-button"
                  >
                    {isSubmitting ? 'Booking...' : 'Confirm Booking'}
                  </button>
                </div>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default BookingPage;
